import React, { FunctionComponent, ReactNode, useMemo } from 'react';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import Translate from 'ui/atoms/translate';
import {
  AdminApi,
  AdminFriendVouchersListOrderingEnum,
  AdminFriendVouchersListRequest,
  AdminFriendVouchersValuesRetrieveFieldEnum,
  AdminVoucher,
} from 'api';
import StatusTag from 'ui/atoms/status-tag';
import Link from 'ui/atoms/link';
import * as Styled from './styled';
import WithDataRecord from 'hoc/WithDataRecord';
import CopyButton from 'src/ui/molecules/copy-button';
import useApiCall from 'src/hooks/use-api-call';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { Search } from 'src/ui/molecules/filtering';

export interface FriendReferralsTableProps extends AdminFriendVouchersListRequest {
  goToDetails: (referrerId: string) => void;
  title?: ReactNode;
  hasPermission: boolean;
  loading?: boolean;
  defaultLimit?: number;
}

const FriendReferralsTable: FunctionComponent<FriendReferralsTableProps> = WithDataRecord<AdminApi, AdminVoucher>(
  AdminApi,
)(
  ({ data, ordering, onOrderBy, goToDetails, title, hasPermission, loading, paginationProps }) => {
    const { makeAuthenticatedApi } = useApiCall();
    const translate = useTranslateWithStringArgs();
    const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

    const referralsStatusFilterOptions = useMemo(() => {
      return ['true', 'false'].map((active) => ({
        id: active,
        name: translate(`issuerFriendReferrals.status.${active === 'true' ? 'active' : 'deactivated'}`),
      }));
    }, [translate]);

    return (
      <>
        <DataRecordTable
          data={data}
          ordering={ordering}
          onOrderBy={onOrderBy}
          title={title}
          loading={loading}
          hasPermission={hasPermission}
          paginationProps={paginationProps}
          onGoToDetails={goToDetails}
          searchComponent={
            <Search
              input="search"
              defaultField="search"
              label={translate('filters.labels.nameOrReferral')}
              name="search"
            />
          }
          cells={[
            {
              title: <Translate name={`issuerFriendReferrals.table.referrer`} />,
              render: (referral) => (
                <Styled.ActionIconCell>
                  {referral.owningInvestor.investorName}
                  <Link
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    href={`/dashboard/investor/${referral.owningInvestor.id}`}
                    className="external-link"
                    external
                  />
                </Styled.ActionIconCell>
              ),
              orderingFieldName: AdminFriendVouchersListOrderingEnum.OWNING_INVESTOR_NAME_ASC,
              type: CellTypeEnum.text,
              filterName: 'owningInvestorName',
              searchFieldName: AdminFriendVouchersValuesRetrieveFieldEnum.OWNING_INVESTOR_NAME,
              getFilterData: (params: any) => adminApi.adminFriendVouchersValuesRetrieve(params),
            },
            {
              title: <Translate name={`issuerFriendReferrals.table.voucherCode`} />,
              render: (referral) => <span>{referral.code} </span>,
              copyValue: (referral) => <CopyButton valueToCopy={referral.code} />,
              orderingFieldName: AdminFriendVouchersListOrderingEnum.CODE_ASC,
              type: CellTypeEnum.text,
              filterName: 'code',
              searchFieldName: AdminFriendVouchersValuesRetrieveFieldEnum.CODE,
              getFilterData: (params: any) => adminApi.adminFriendVouchersValuesRetrieve(params),
            },
            {
              title: <Translate name={`issuerFriendReferrals.table.useNumber`} />,
              render: (referral) => referral.alreadyUsed,
              alignContent: 'right',
              orderingFieldName: AdminFriendVouchersListOrderingEnum.ALREADY_USED_ASC,
              type: CellTypeEnum.number,
              filterName: 'alreadyUsed',
            },
            {
              title: <Translate name={`issuerFriendReferrals.table.status`} />,
              render: (referral) => (
                <StatusTag variant={referral.active ? 'success' : 'danger'} truncate={true}>
                  <Translate name={`issuerFriendReferrals.status.${referral.active ? 'active' : 'deactivated'}`} />
                </StatusTag>
              ),
              width: 135,
              orderingFieldName: AdminFriendVouchersListOrderingEnum.ACTIVE_ASC,
              filterName: 'active',
              getFilterData: () => {
                return referralsStatusFilterOptions;
              },
            },
          ]}
        />
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.adminFriendVouchersList({
      offset,
      limit,
      ordering: ordering as AdminFriendVouchersListOrderingEnum,
      ...props,
    });
  },
);

export default FriendReferralsTable;
